/**
 * Framework-free time foundation shared by browser and API.
 * Instants retain millisecond precision; presentation alone omits seconds.
 */
export type ZonedParts = {
  year: number; month: number; day: number;
  hour: number; minute: number; second: number; ms: number; dow: number;
};
export type Instant = Date | string | number;
export type TimeContext =
  | { kind: 'site'; timeZone: string | null | undefined }
  | { kind: 'device' };
export type DateStyle = 'full' | 'weekday' | 'compact';
export type DisplayOptions = { dateStyle?: DateStyle; includeZone?: boolean | 'abbreviation' };

const aliases: Record<string, string> = {
  AEST: 'Australia/Sydney', AEDT: 'Australia/Sydney',
  ACST: 'Australia/Adelaide', ACDT: 'Australia/Adelaide', AWST: 'Australia/Perth'
};
const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatters = new Map<string, Intl.DateTimeFormat>();

export function isValidIanaTimeZone(zone: string): boolean {
  if (!zone || !zone.trim()) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: zone }); return true; }
  catch { return false; }
}

export function strictResolveTimeZone(zone: string | null | undefined): string | null {
  const value = (zone ?? '').trim();
  if (!value) return null;
  if (isValidIanaTimeZone(value)) return value;
  const mapped = aliases[value.toUpperCase()];
  return mapped && isValidIanaTimeZone(mapped) ? mapped : null;
}

export function deviceTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

/** A fallback is presentation-only and must be labelled by the formatter. */
export function resolveDisplayZone(context: TimeContext = { kind: 'device' }):
  { timeZone: string; fallback: boolean } {
  if (context.kind === 'device') return { timeZone: deviceTimezone(), fallback: false };
  const zone = strictResolveTimeZone(context.timeZone);
  return { timeZone: zone ?? deviceTimezone(), fallback: zone === null };
}

export function getZonedParts(date: Date, timeZone: string): ZonedParts {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    });
    // Bound the cache even when callers supply many equivalent zone identifiers.
    if (formatters.size >= 128) formatters.delete(formatters.keys().next().value!);
    formatters.set(timeZone, formatter);
  }
  const values: Record<string, string> = {};
  for (const part of formatter.formatToParts(date)) {
    if (part.type !== 'literal') values[part.type] = part.value;
  }
  return {
    year: Number(values.year), month: Number(values.month), day: Number(values.day),
    hour: Number(values.hour), minute: Number(values.minute), second: Number(values.second),
    ms: date.getUTCMilliseconds(), dow: weekdays.indexOf(values.weekday ?? '')
  };
}

/** UTC arithmetic for calendar fields, including years 0000–0099. */
function calendarEpoch(year: number, month: number, day: number, hour = 0, minute = 0, second = 0, ms = 0): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, ms);
  return date.getTime();
}

function wallEpoch(date: Date, zone: string): number {
  const p = getZonedParts(date, zone);
  return calendarEpoch(p.year, p.month, p.day, p.hour, p.minute, p.second, p.ms);
}

/**
 * Temporal-compatible disambiguation: first occurrence of a fold; shift gaps
 * forward by the actual offset change (including Lord Howe's 30 minutes).
 * Probe both sides of the wall day, verify every candidate using IANA rules.
 * Opening-hours boundaries intentionally use their separate edge policy.
 */
export function zonedWallTimeToUtc(
  year: number, month: number, day: number, hour: number, minute: number,
  second: number, ms: number, timeZone: string
): Date {
  if (!isValidIanaTimeZone(timeZone)) throw new RangeError('Invalid site timezone');
  const target = calendarEpoch(year, month, day, hour, minute, second, ms);
  const check = new Date(target);
  if (![year, month, day, hour, minute, second, ms].every(Number.isInteger) ||
      check.getUTCFullYear() !== year || check.getUTCMonth() + 1 !== month ||
      check.getUTCDate() !== day || check.getUTCHours() !== hour ||
      check.getUTCMinutes() !== minute || check.getUTCSeconds() !== second ||
      check.getUTCMilliseconds() !== ms) throw new RangeError('Invalid wall-clock date/time');

  const offsets = new Set<number>();
  // Covers offsets up to 24h and whole-day date-line gaps as well as DST.
  for (const hours of [-48, -24, -12, 0, 12, 24, 48]) {
    const probe = target + hours * 3_600_000;
    offsets.add(wallEpoch(new Date(probe), timeZone) - probe);
  }
  const candidates = [...offsets].map(offset => {
    const instant = target - offset;
    return { instant, wall: wallEpoch(new Date(instant), timeZone) };
  });
  const exact = candidates.filter(c => c.wall === target).sort((a, b) => a.instant - b.instant);
  if (exact[0]) return new Date(exact[0].instant);
  const after = candidates.filter(c => c.wall > target).sort((a, b) => a.wall - b.wall || a.instant - b.instant);
  if (after[0]) return new Date(after[0].instant);
  throw new RangeError('Cannot resolve wall-clock date/time');
}

/** Calendar dates never enter a device/site timezone conversion. */
export function parseCalendarDate(value: string): Pick<ZonedParts, 'year' | 'month' | 'day' | 'dow'> {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) throw new RangeError('Invalid calendar date');
  const year = Number(match[1]), month = Number(match[2]), day = Number(match[3]);
  const date = new Date(calendarEpoch(year, month, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day)
    throw new RangeError('Invalid calendar date');
  return { year, month, day, dow: date.getUTCDay() };
}

function dateText(p: Pick<ZonedParts, 'year' | 'month' | 'day' | 'dow'>, style: DateStyle = 'full'): string {
  const date = `${p.day} ${months[p.month - 1]}`;
  return style === 'compact' ? `${weekdays[p.dow]} ${date}` :
    style === 'weekday' ? `${weekdays[p.dow]} ${date} ${p.year}` : `${date} ${p.year}`;
}
function timeText(p: Pick<ZonedParts, 'hour' | 'minute'>): string {
  return `${p.hour % 12 || 12}:${String(p.minute).padStart(2, '0')} ${p.hour < 12 ? 'am' : 'pm'}`;
}
function asInstant(value: Instant): Date {
  // Reject wall-clock/date-only strings: their context must be resolved explicitly.
  if (typeof value === 'string' && !/(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(value.trim()))
    throw new RangeError('Display timestamp must be an absolute instant');
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) throw new RangeError('Invalid display timestamp');
  return date;
}

/** Normalize an absolute ISO instant without dropping any fractional digits. */
export function absoluteIsoToUtcString(value: string): string {
  const normalized = asInstant(value).toISOString();
  const fraction = /\.(\d+)(?=[Zz]|[+-]\d{2}:?\d{2}$)/.exec(value.trim())?.[1];
  return fraction && fraction.length > 3
    ? normalized.replace(/\.\d{3}Z$/, '.' + fraction + 'Z')
    : normalized;
}

export function timezoneAbbreviation(timeZone: string, instant: Instant = new Date()): string {
  const zone = strictResolveTimeZone(timeZone);
  const abbreviation = new Intl.DateTimeFormat('en-AU', {
    timeZone: zone ?? deviceTimezone(), timeZoneName: 'short'
  }).formatToParts(asInstant(instant)).find(p => p.type === 'timeZoneName')?.value ?? '';
  return abbreviation + (zone ? '' : ' (device time; site timezone unavailable)');
}

export function timezoneLabel(timeZone: string, instant: Instant = new Date()): string {
  const zone = strictResolveTimeZone(timeZone);
  const resolved = zone ?? deviceTimezone();
  const city = resolved.slice(resolved.lastIndexOf('/') + 1).replaceAll('_', ' ');
  const abbreviation = new Intl.DateTimeFormat('en-AU', {
    timeZone: resolved, timeZoneName: 'short'
  }).formatToParts(asInstant(instant)).find(p => p.type === 'timeZoneName')?.value;
  return `${abbreviation ? abbreviation + ' — ' : ''}${city}${zone ? '' : ' (device time; site timezone unavailable)'}`;
}
function suffix(zone: ReturnType<typeof resolveDisplayZone>, date: Date, includeZone: DisplayOptions['includeZone'] = false): string {
  if (zone.fallback) return ' (device time; site timezone unavailable)';
  if (includeZone === 'abbreviation') return ` ${timezoneAbbreviation(zone.timeZone, date)}`;
  return includeZone ? ` ${timezoneLabel(zone.timeZone, date)}` : '';
}

export function formatCalendarDate(value: string, style: DateStyle = 'full'): string {
  return dateText(parseCalendarDate(value), style);
}
export function formatDate(value: Instant, context?: TimeContext, options: DisplayOptions = {}): string {
  const date = asInstant(value), zone = resolveDisplayZone(context);
  return dateText(getZonedParts(date, zone.timeZone), options.dateStyle) + suffix(zone, date, options.includeZone);
}
export function formatTime(value: Instant, context?: TimeContext, options: DisplayOptions = {}): string {
  const date = asInstant(value), zone = resolveDisplayZone(context);
  return timeText(getZonedParts(date, zone.timeZone)) + suffix(zone, date, options.includeZone);
}
export function formatDateTime(value: Instant, context?: TimeContext, options: DisplayOptions = {}): string {
  const date = asInstant(value), zone = resolveDisplayZone(context), p = getZonedParts(date, zone.timeZone);
  return `${dateText(p, options.dateStyle)}, ${timeText(p)}` + suffix(zone, date, options.includeZone);
}
export function formatDateTimeRange(start: Instant, end: Instant, context?: TimeContext, options: DisplayOptions = {}): string {
  const from = asInstant(start), to = asInstant(end), zone = resolveDisplayZone(context);
  const a = getZonedParts(from, zone.timeZone), b = getZonedParts(to, zone.timeZone);
  const sameDay = a.year === b.year && a.month === b.month && a.day === b.day;
  const first = `${dateText(a, options.dateStyle)}, ${timeText(a)}`;
  const last = sameDay ? timeText(b) : `${dateText(b, options.dateStyle)}, ${timeText(b)}`;
  // Label both ends when requested: a range may cross an offset transition.
  if (options.includeZone && !zone.fallback)
    return first + suffix(zone, from, options.includeZone) + ' → ' + last + suffix(zone, to, options.includeZone);
  return first + ' → ' + last + suffix(zone, from);
}
